import Vue from 'vue'
import Vuex from 'vuex'
import DashboardApi from '../api/dashboard';
import CoreApi from '../api/core';
import checkup from './checkup';
import questionnaire from './questionnaire';

Vue.use(Vuex);

const env = process.env;

const dashboardApi = new DashboardApi(
  env.VUE_APP_API_DASHBOARD_URL,
  env.VUE_APP_API_DASHBOARD_TIMEOUT
);

const coreApi = new CoreApi(
  env.VUE_APP_API_CORE_URL,
  parseInt(env.VUE_APP_API_CORE_TIMEOUT)
);

const state = {
  view: '',
  error: '',
  isFirstLogin: localStorage.isFirstLogin !== 'false',
  token: localStorage.token || '',
  profile: null,
  prescriptions: [],
  prescriptionsLoaded: false,
  healthStatus: [],
  contacts: {},
  times: [],
  health: [],
  recommendations: [],
  strategies: [],
  conclusions: [],
  diagnoses: [],
  functionals: [],
};

const getters = {
  hasAuth (state) {
    if (typeof state.token !== 'string') {
      return false;
    }

    return state.token.trim() !== '' && state.token.trim() !== 'undefined';
  },

  isFirstLogin (state) {
    return state.isFirstLogin !== false;
  },

  hasEarlyAccess (state) {
    return state.profile !== null && state.profile.earlyAccess;
  },

  getFeature: state => flag => {
    if (typeof state.profile === 'undefined' || state.profile === null) {
      return null;
    }

    const {features} = state.profile;

    if (typeof features === 'undefined') {
      return null;
    }

    if (Array.isArray(features) && features.includes(flag)) {
      return true;
    }

    if (typeof features[flag] === 'undefined') {
      return null;
    }

    return state.profile.features[flag];
  },
};

const mutations = {
  SET_FIRST_LOGIN(state) {
    localStorage.isFirstLogin = false;
    state.isFirstLogin = false;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  SET_VIEW(state, view) {
    state.view = view;
  },

  SET_TOKEN: (state, token) => {
    localStorage.token = token;
    state.token = token;
  },

  SET_PROFILE: (state, profile) => {
    state.profile = profile;

    state.profile.image.url = env.VUE_APP_API_DASHBOARD_URL + state.profile.image.url;
  },

  SET_PRESCRIPTIONS: (state, payload) => {
    state.prescriptions = payload;
    state.prescriptionsLoaded = true;

    state.prescriptions.sort((a, b) => {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    });
  },

  SET_HEALTH_STATUS: (state, payload) => {
    state.healthStatus = payload;
  },

  SET_STRATEGIES: (state, payload) => {
    state.strategies = payload;
  },

  SET_DIAGNOSES: (state, payload) => {
    if (Array.isArray(payload)) {
      state.diagnoses = payload;
    }
  },

  SET_CONCLUSIONS: (state, payload) => {
    if (Array.isArray(payload)) {
      state.conclusions = payload;
    }
  },

  SET_FUNCTIONALS: (state, payload) => {
    if (Array.isArray(payload)) {
      state.functionals = payload;
    }
  },

  SET_TIMES: (state, payload) => {
    state.times = payload;
  },

  SET_HEALTH: (state, payload) => {
    state.health = payload;
  },

  SET_RECOMMENDATIONS: (state, payload) => {
    for (const item of payload) {
      if (item.image) {
        item.image.url = env.VUE_APP_API_DASHBOARD_URL + item.image.url;
        item.image.file = new Image();
        item.image.file.src = item.image.url;
      }

      for (const content of item.contents) {
        if (content.image) {
          content.image.url = env.VUE_APP_API_DASHBOARD_URL + content.image.url;
          content.image.file = new Image();
          content.image.file.src = content.image.url;
        }
      }
    }

    state.recommendations = payload;
  },

  SET_CONTACTS: (state, payload) => {
    for (const type in payload) {
      for (const contact of payload[type]) {
        contact.name = `${contact.firstname} ${contact.lastname}`;
        contact.image.url = env.VUE_APP_API_DASHBOARD_URL + contact.image.url
      }
    }

    state.contacts = payload;
  },
};

const actions = {
  ERROR: async (context, error) => {
    const message = error && error.message ? error.message : 'Something went wrong';

    if (error.response && error.response.status && [401, 403].includes(error.response.status)) {
      context.commit('SET_TOKEN', '');
    } else {
      context.commit('SET_ERROR', message);
    }
  },

  CHANGE_VIEW: async (context, payload) => {
    context.commit('SET_VIEW', payload);
  },

  LOG_IN: async (context, payload) => {
    const { login, password } = payload;
    const response = await dashboardApi.logIn(login, password);

    context.commit('SET_TOKEN', response.token);
  },

  LOG_OUT: async (context) => {
    const token = context.state.token;

    context.commit('SET_TOKEN', '');

    await dashboardApi.logOut(token);
  },

  LOAD_PROFILE: async (context) => {
    const token = context.state.token;
    const response = await dashboardApi.getProfile(token);

    context.commit('SET_PROFILE', response);
  },

  LOAD_PRESCRIPTIONS: async (context) => {
    const token = context.state.token;
    const response = await dashboardApi.getPrescriptions(token);

    context.commit('SET_PRESCRIPTIONS', response.items);
  },

  LOAD_TIMES: async (context) => {
    const token = context.state.token;
    const response = await dashboardApi.getTimes(token);

    context.commit('SET_TIMES', response.items);
  },

  LOAD_HEALTH: async (context) => {
    const token = context.state.token;
    const response = await coreApi.getHealth(token);

    context.commit('SET_HEALTH', response.items);
  },

  LOAD_HEALTH_STATUS: async (context) => {
    const token = context.state.token;
    const response = await coreApi.getHealthStatus(token);

    context.commit('SET_HEALTH_STATUS', response.items);
  },

  LOAD_STRATEGIES: async (context) => {
    const token = context.state.token;
    const response = await coreApi.getStrategies(token);

    context.commit('SET_STRATEGIES', response);
  },

  LOAD_DIAGNOSES: async (context) => {
    const token = context.state.token;
    const response = await coreApi.getDiagnoses(token);

    context.commit('SET_DIAGNOSES', response.items);
  },

  LOAD_CONCLUSIONS: async (context) => {
    const token = context.state.token;
    const response = await coreApi.getConclusions(token);

    context.commit('SET_CONCLUSIONS', response.items);
  },

  LOAD_FUNCTIONALS: async (context) => {
    const token = context.state.token;
    const response = await coreApi.getFunctionals(token);

    context.commit('SET_FUNCTIONALS', response.items);
  },

  LOAD_RECOMMENDATIONS: async (context) => {
    const token = context.state.token;
    const response = await dashboardApi.getRecommendations(token);

    context.commit('SET_RECOMMENDATIONS', response.items);
  },

  LOAD_CONTACTS: async (context) => {
    const token = context.state.token;
    const response = await dashboardApi.getContacts(token);

    context.commit('SET_CONTACTS', response);
  },

  START_PRESCRIPTION: async (context, payload) => {
    const token = context.state.token;
    const { drugPrescriptionId, startDate } = payload;

    await dashboardApi.startPrescription(token, drugPrescriptionId, startDate);

    context.dispatch('LOAD_PRESCRIPTIONS');
  },

  SET_BOUGHT_PRESCRIPTION: async (context, payload) => {
    const token = context.state.token;

    await dashboardApi.setBoughtPrescription(token, payload);

    context.dispatch('LOAD_PRESCRIPTIONS');
  },

  UNSET_BOUGHT_PRESCRIPTION: async (context, payload) => {
    const token = context.state.token;

    await dashboardApi.unsetBoughtPrescription(token, payload);

    context.dispatch('LOAD_PRESCRIPTIONS');
  },
};

export default new Vuex.Store({
  state,
  getters,
  mutations,
  actions,
  modules: {
    checkup,
    questionnaire,
  },
});
